import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { logger } from "../lib/logger";
import {
  LayoutDashboard,
  ShoppingCart,
  IndianRupee,
  MapPin,
  Target,
  UserPlus,
  Truck,
  CalendarRange,
  Users2,
  Package,
  Bell,
  FileSpreadsheet,
  CheckSquare,
  LogOut,
  Sprout,
  Menu,
  X,
  Wheat,
  Tags,
  TrendingUp,
  CalendarDays,
  Wallet,
  Receipt,
  KeyRound,
  Download,
  ClipboardList,
  WifiOff,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { Toaster, toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { countQueue } from "../lib/offlineQueue";
import { drainQueue, installOfflineSync } from "../lib/offlineSync";
import { useInstallPrompt } from "../hooks/useInstallPrompt";

const linkBase =
  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors";
const inactive = "text-[#5C6B5E] hover:bg-[#F2EDE4] hover:text-[#1A2E1C]";
const active = "bg-[#1E3F20] text-white shadow-sm";

function NavItem({ to, icon: Icon, label, onClick, testid, badge }) {
  return (
    <NavLink
      to={to}
      end
      onClick={onClick}
      data-testid={testid}
      className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}
    >
      <Icon className="w-4 h-4" strokeWidth={2.25} />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span
          data-testid={`${testid}-badge`}
          className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#C15A2C] text-white"
        >
          {badge}
        </span>
      )}
    </NavLink>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { canInstall, install } = useInstallPrompt();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const role = user?.role;
  const isHR = role === "hr";
  const isAdminOrManager = role === "admin" || role === "manager" || role === "director";
  const canManageHolidays = ["admin", "director", "hr"].includes(role);
  const isTeamLead = ["admin", "director", "manager", "rm_and_above", "sales_manager"].includes(role);
  const canApproveExpenses = isTeamLead || isHR;
  const [pendingAssigned, setPendingAssigned] = useState(0);
  const [pwOpen, setPwOpen] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pendingSync, setPendingSync] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    installOfflineSync();
    const refreshCount = async () => {
      try {
        setPendingSync(await countQueue());
      } catch (err) {
        // Non-fatal — IndexedDB may be unavailable in some private modes.
        // The badge just stays at its last known count until the next tick.
        logger.debug("offline badge count refresh failed:", err?.message);
      }
    };
    refreshCount();
    const onChange = () => refreshCount();
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("tg-outbox-changed", onChange);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const id = setInterval(refreshCount, 15_000);
    return () => {
      window.removeEventListener("tg-outbox-changed", onChange);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      clearInterval(id);
    };
  }, []);

  const handleSyncNow = async () => {
    if (!online) {
      toast.error("Still offline — will auto-sync when connection returns.");
      return;
    }
    setSyncing(true);
    try {
      await drainQueue();
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const r = await api.get("/todos/pending-count");
        if (!cancelled) setPendingAssigned(r.data?.pending_assigned || 0);
      } catch (err) {
        // Non-fatal — the badge just stays stale for this tick. Poll retries every 60s.
        logger.debug("todos/pending-count refresh failed:", err?.message);
      }
    };
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const close = () => setOpen(false);

  return (
    <div className="min-h-screen flex bg-[#F9F8F6]" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-72 bg-white border-r border-[#E5E2DC] transform transition-transform lg:static lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-5 py-5 border-b border-[#E5E2DC] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1E3F20] grid place-items-center">
              <Sprout className="w-5 h-5 text-[#DDA15E]" strokeWidth={2.25} />
            </div>
            <div>
              <div className="font-heading font-black text-lg leading-none">Taraigold</div>
              <div className="text-[10px] font-bold tracking-widest uppercase text-[#5C6B5E] mt-1">
                Field Force
              </div>
            </div>
          </div>
          <button className="lg:hidden text-[#5C6B5E]" onClick={close} aria-label="Close menu">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-3 space-y-1 overflow-y-auto" style={{ maxHeight: "calc(100vh - 170px)" }}>
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C6B5E] px-3 pt-2 pb-1">
            Overview
          </div>
          <NavItem to="/" icon={LayoutDashboard} label="Dashboard" onClick={close} testid="nav-dashboard" />
          <NavItem to="/notifications" icon={Bell} label="Notifications" onClick={close} testid="nav-notifications" />

          <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C6B5E] px-3 pt-4 pb-1">
            Field Work
          </div>
          {!isHR && <NavItem to="/orders" icon={ShoppingCart} label="Orders" onClick={close} testid="nav-orders" />}
          {!isHR && <NavItem to="/collections" icon={IndianRupee} label="Collections" onClick={close} testid="nav-collections" />}
          <NavItem to="/daily-report" icon={MapPin} label="Daily Report" onClick={close} testid="nav-daily-report" />
          {!isHR && <NavItem to="/leads" icon={UserPlus} label="Leads" onClick={close} testid="nav-leads" />}
          {!isHR && <NavItem to="/farmers" icon={Wheat} label="Farmers" onClick={close} testid="nav-farmers-field" />}
          {!isHR && <NavItem to="/demonstrations" icon={Sprout} label="Demonstrations" onClick={close} testid="nav-demos" />}
          {!isHR && <NavItem to="/farmer-meetings" icon={Wheat} label="Farmer Meetings" onClick={close} testid="nav-fm" />}
          {!isHR && <NavItem to="/br-updates" icon={CalendarRange} label="BR" onClick={close} testid="nav-br" />}
          {!isHR && <NavItem to="/todos" icon={CheckSquare} label="To-Do" onClick={close} testid="nav-todos" badge={pendingAssigned} />}
          {!isHR && <NavItem to="/dispatches" icon={Truck} label="Dispatches" onClick={close} testid="nav-dispatches" />}
          <NavItem to="/expenses/mine" icon={Wallet} label="My Expenses" onClick={close} testid="nav-my-expenses" />
          <NavItem to="/leaves" icon={CalendarDays} label="Leaves" onClick={close} testid="nav-leaves" />
          <NavItem to="/attendance" icon={CalendarDays} label="Attendance" onClick={close} testid="nav-attendance" />
          {canManageHolidays && <NavItem to="/holidays" icon={CalendarDays} label="Holidays" onClick={close} testid="nav-holidays" />}

          {!isHR && (
            <>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C6B5E] px-3 pt-4 pb-1">
                Targets
              </div>
              <NavItem to="/targets" icon={Target} label="Targets & Achievement" onClick={close} testid="nav-targets" />
            </>
          )}
          {isTeamLead && (
            <>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C6B5E] px-3 pt-4 pb-1">
                Reviews
              </div>
              <NavItem to="/reviews/daily-report" icon={ClipboardList} label="Team Daily Report" onClick={close} testid="nav-team-daily-report" />
              <NavItem to="/team-performance" icon={TrendingUp} label="Team Performance" onClick={close} testid="nav-team-performance" />
              <NavItem to="/reports" icon={FileSpreadsheet} label="Reports" onClick={close} testid="nav-reports" />
            </>
          )}
          {canApproveExpenses && (
            <NavItem to="/expenses/approvals" icon={Receipt} label="Expense Approvals" onClick={close} testid="nav-expense-approvals" />
          )}

          {isAdminOrManager && (
            <>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C6B5E] px-3 pt-4 pb-1">
                Masters
              </div>
              <NavItem to="/customers" icon={Users2} label="Customers" onClick={close} testid="nav-customers" />
              <NavItem to="/farmers" icon={Wheat} label="Farmers" onClick={close} testid="nav-farmers" />
              <NavItem to="/products" icon={Package} label="Products" onClick={close} testid="nav-products" />
              <NavItem to="/price-lists" icon={Tags} label="Price Lists" onClick={close} testid="nav-price-lists" />
              {(role === "admin" || role === "director") && (
                <NavItem to="/users" icon={Users2} label="Users" onClick={close} testid="nav-users" />
              )}
            </>
          )}
        </nav>

        <div className="absolute bottom-0 inset-x-0 p-4 border-t border-[#E5E2DC] bg-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#A3B18A]/30 text-[#1E3F20] font-bold grid place-items-center">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name || user?.email}</div>
              <div className="text-xs uppercase tracking-wider text-[#5C6B5E]">{role}</div>
            </div>
          </div>
          {canInstall && (
            <button
              onClick={async () => {
                const ok = await install();
                if (ok) toast.success("Taraigold installed on your device");
              }}
              data-testid="install-app-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#DDA15E] text-[#1A2E1C] rounded-lg px-3 py-2 text-sm font-bold hover:bg-[#C98E4C] mb-2"
            >
              <Download className="w-4 h-4" /> Install app
            </button>
          )}
          <button
            onClick={() => setPwOpen(true)}
            data-testid="change-password-btn"
            className="w-full flex items-center justify-center gap-2 border border-[#E5E2DC] text-[#1A2E1C] rounded-lg px-3 py-2 text-sm font-semibold hover:bg-[#F2EDE4] mb-2"
          >
            <KeyRound className="w-4 h-4" /> Change password
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 border border-[#E5E2DC] text-[#1A2E1C] rounded-lg px-3 py-2 text-sm font-semibold hover:bg-[#F2EDE4]"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top-right floating user actions — visible on every page */}
        <div className="fixed top-3 right-3 z-40 hidden lg:flex items-center gap-2 bg-white/95 backdrop-blur border border-[#E5E2DC] rounded-full pl-3 pr-1 py-1 shadow-sm" data-testid="topbar-actions">
          <span className="text-xs font-semibold text-[#1A2E1C] max-w-[160px] truncate">{user?.name || user?.email}</span>
          <button
            onClick={() => setPwOpen(true)}
            data-testid="topbar-change-password"
            title="Change password"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-[#1E3F20] hover:bg-[#F2EDE4]"
          >
            <KeyRound className="w-3.5 h-3.5" /> Password
          </button>
          <button
            onClick={handleLogout}
            data-testid="topbar-logout"
            title="Sign out"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-white bg-[#1E3F20] hover:bg-[#152D17]"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
        <header className="lg:hidden sticky top-0 z-30 backdrop-blur-xl bg-[#F9F8F6]/80 border-b border-[#E5E2DC] px-4 py-3 flex items-center justify-between">
          <button onClick={() => setOpen(true)} className="text-[#1A2E1C]" aria-label="Open menu" data-testid="open-menu">
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <Sprout className="w-5 h-5 text-[#1E3F20]" />
            <span className="font-heading font-black">Taraigold</span>
          </div>
          <div className="w-6" />
        </header>

        <main className="flex-1 p-4 sm:p-8 animate-fade-in-up">
          <Outlet />
        </main>
      </div>

      {/* Offline / pending-sync indicator */}
      {(!online || pendingSync > 0) && (
        <div
          data-testid="offline-indicator"
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border text-xs font-medium bg-white"
          style={{ borderColor: !online ? "#C15A2C" : "#D9C58E" }}
        >
          <WifiOff
            className="w-4 h-4"
            style={{ color: !online ? "#C15A2C" : "#8B7A3B" }}
          />
          <span data-testid="offline-indicator-text" className="text-[#1A2E1C]">
            {!online ? "Offline" : "Online"}
            {pendingSync > 0 && (
              <>
                {" · "}
                <span data-testid="offline-pending-count" className="font-bold">
                  {pendingSync}
                </span>
                {" pending"}
              </>
            )}
          </span>
          {pendingSync > 0 && online && (
            <button
              onClick={handleSyncNow}
              disabled={syncing}
              data-testid="offline-sync-now"
              className="ml-1 px-2 py-1 rounded-full bg-[#1E3F20] text-white text-[10px] font-bold uppercase tracking-widest hover:bg-[#152D17] disabled:opacity-60 flex items-center gap-1"
            >
              <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing" : "Sync now"}
            </button>
          )}
        </div>
      )}

      {/* Overlay for mobile */}
      {open && <div onClick={close} className="fixed inset-0 bg-black/40 z-30 lg:hidden" />}
      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
      <Toaster richColors position="top-right" />
    </div>
  );
}


function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 6) return toast.error("New password must be at least 6 characters");
    if (form.next !== form.confirm) return toast.error("New password and confirmation do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password updated");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4"
      onClick={onClose}
      data-testid="change-password-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-bold flex items-center gap-2">
            <KeyRound className="w-5 h-5" /> Change password
          </h3>
          <button onClick={onClose} className="text-[#5C6B5E] hover:text-[#1A2E1C]" data-testid="change-password-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#5C6B5E]">Current password</label>
            <input
              type="password"
              required
              value={form.current}
              onChange={(e) => setForm({ ...form, current: e.target.value })}
              className="w-full mt-1 border border-[#E5E2DC] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3F20]"
              data-testid="cp-current"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#5C6B5E]">New password</label>
            <input
              type="password"
              required
              minLength={6}
              value={form.next}
              onChange={(e) => setForm({ ...form, next: e.target.value })}
              className="w-full mt-1 border border-[#E5E2DC] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3F20]"
              data-testid="cp-new"
            />
            <p className="text-[11px] text-[#8A8577] mt-1">Minimum 6 characters</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#5C6B5E]">Confirm new password</label>
            <input
              type="password"
              required
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className="w-full mt-1 border border-[#E5E2DC] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3F20]"
              data-testid="cp-confirm"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="border border-[#E5E2DC] text-[#1A2E1C] rounded-lg px-4 py-2 text-sm font-semibold hover:bg-[#F2EDE4]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="bg-[#1E3F20] text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-[#153217] disabled:opacity-60"
              data-testid="cp-submit"
            >
              {busy ? "Saving…" : "Update password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
